import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bridgeApi } from '@/lib/bridgeApi';

const UI_CONFIG_KEY = 'patakare_ui_config';
const UI_CONFIG_UPDATED_EVENT = 'patakare-ui-config-updated';

export interface UIConfig {
  // Buttons
  btn_primary_bg: string;
  btn_primary_text: string;
  btn_secondary_bg: string;
  btn_secondary_text: string;
  btn_border_radius: number;
  // Cards
  card_border_radius: number;
  card_shadow: string;
  card_border_color: string;
  card_bg: string;
  card_hover_shadow: boolean;
  // Badges
  badge_verified_bg: string;
  badge_verified_text: string;
  badge_open_bg: string;
  badge_open_text: string;
  badge_closed_bg: string;
  badge_closed_text: string;
  badge_border_radius: number;
  badge_show_verified_icon: boolean;
  // Images
  img_border_radius: number;
  img_aspect_ratio: string;
  img_object_fit: string;
  img_border_color: string;
  img_border_width: number;
  img_shadow: string;
  img_placeholder_bg: string;
  // Gradients
  gradient_hero: string;
  gradient_primary: string;
  gradient_cta: string;
  gradient_card: string;
  // Section / Page backgrounds
  section_bg: string;
  auth_bg: string;
  footer_bg: string;
  footer_text: string;
  // Category cards
  category_card_radius: number;
  category_icon_radius: number;
  // Dashboard
  dashboard_accent: string;
}

const defaults: UIConfig = {
  btn_primary_bg: 'hsl(152 62% 44%)',
  btn_primary_text: '#ffffff',
  btn_secondary_bg: '#f1f5f9',
  btn_secondary_text: '#334155',
  btn_border_radius: 9999,
  card_border_radius: 16,
  card_shadow: 'sm',
  card_border_color: '',
  card_bg: '#ffffff',
  card_hover_shadow: true,
  badge_verified_bg: 'hsl(152 62% 44%)',
  badge_verified_text: '#ffffff',
  badge_open_bg: 'hsl(152 62% 44%)',
  badge_open_text: '#ffffff',
  badge_closed_bg: '#ef4444',
  badge_closed_text: '#ffffff',
  badge_border_radius: 9999,
  badge_show_verified_icon: true,
  img_border_radius: 16,
  img_aspect_ratio: '16/10',
  img_object_fit: 'cover',
  img_border_color: '',
  img_border_width: 0,
  img_shadow: 'none',
  img_placeholder_bg: '#ffffff',
  // Gradients
  gradient_hero: 'linear-gradient(135deg, hsl(213 69% 35%) 0%, hsl(213 60% 45%) 40%, hsl(152 62% 40%) 100%)',
  gradient_primary: 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))',
  gradient_cta: 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))',
  gradient_card: 'linear-gradient(180deg, #ffffff, hsl(207 47% 97%))',
  // Sections
  section_bg: 'transparent',
  auth_bg: 'linear-gradient(135deg, hsl(207 47% 97%), white, hsl(213 69% 39% / 0.05))',
  footer_bg: 'hsl(215 30% 12%)',
  footer_text: '#ffffff',
  // Category
  category_card_radius: 16,
  category_icon_radius: 16,
  // Dashboard
  dashboard_accent: 'hsl(213 69% 39%)',
};

const mergeConfig = (partial?: Partial<UIConfig> | null) => ({
  ...defaults,
  ...(partial ?? {}),
});

const readStoredUIConfig = (): UIConfig => {
  try {
    const stored = localStorage.getItem(UI_CONFIG_KEY);
    if (stored) {
      return mergeConfig(JSON.parse(stored));
    }
  } catch {
    // ignore malformed local storage
  }

  return defaults;
};

export const emitUIConfigUpdated = (config: UIConfig) => {
  window.dispatchEvent(new CustomEvent(UI_CONFIG_UPDATED_EVENT, { detail: config }));
};

const UI_CONFIG_QUERY_KEY = ['ui-config'] as const;

// Was plain useState+useEffect — every component calling this hook (every
// single ListingCardV2 on a page, so 8+ on the home page alone) fired its
// OWN independent fetch on mount with zero sharing between them, all asking
// the backend the exact same question at the exact same time (confirmed
// live 2026-08-20: 9 separate get_ui_config requests on one home page load).
// useQuery with a shared key dedupes concurrent identical requests into one
// real network call no matter how many components mount it at once, and
// caches the result so later mounts in the same session don't even need
// that — same effect as manually sharing this across every card, without
// having to restructure how any of them call the hook.
export const useUIConfig = (): UIConfig => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: UI_CONFIG_QUERY_KEY,
    queryFn: async () => {
      const remoteConfig = await bridgeApi.call<Partial<UIConfig>>('get_ui_config', {});
      // Backend is the single source of truth. A stale localStorage cache
      // (e.g. left over from an admin previewing changes in UIControlPage
      // without saving) must never permanently override the real saved
      // config — that made real listing cards render with an abandoned
      // preview's aspect-ratio/object-fit forever on that one browser.
      const merged = mergeConfig(remoteConfig);
      try {
        localStorage.setItem(UI_CONFIG_KEY, JSON.stringify(merged));
      } catch {
        // localStorage unavailable — cache write is best-effort only
      }
      return merged;
    },
    // Shown instantly from whatever was cached last session while the real
    // fetch above resolves in the background — same "stale-then-fresh" feel
    // the old effect-based version had (instant local read → live backend value).
    initialData: () => readStoredUIConfig(),
    staleTime: 60_000,
  });

  useEffect(() => {
    const storageHandler = (e: StorageEvent) => {
      if (e.key === UI_CONFIG_KEY) {
        queryClient.setQueryData(UI_CONFIG_QUERY_KEY, readStoredUIConfig());
      }
    };

    const customEventHandler = (e: Event) => {
      const nextConfig = (e as CustomEvent<UIConfig>).detail;
      queryClient.setQueryData(UI_CONFIG_QUERY_KEY, mergeConfig(nextConfig));
    };

    window.addEventListener('storage', storageHandler);
    window.addEventListener(UI_CONFIG_UPDATED_EVENT, customEventHandler as EventListener);

    return () => {
      window.removeEventListener('storage', storageHandler);
      window.removeEventListener(UI_CONFIG_UPDATED_EVENT, customEventHandler as EventListener);
    };
  }, [queryClient]);

  return data ?? defaults;
};

/** Helper: map shadow name to tailwind class */
export const getShadowClass = (shadow: string) => {
  switch (shadow) {
    case 'none': return '';
    case 'sm': return 'shadow-sm';
    case 'md': return 'shadow-md';
    case 'lg': return 'shadow-lg';
    case 'xl': return 'shadow-xl';
    default: return 'shadow-sm';
  }
};

export { defaults as uiConfigDefaults };
